import React, { useMemo } from "react";
import { useI18n } from "@/i18n/hooks";
import { MailIcon, Button } from "../../components";
import { OptimizedImage } from "../Base/OptimizedImage";
import { useUserDataFlattened } from "../../modules";
import { usePlatformLogo } from "../../modules/homeDisplay/hooks";
import { useHomeDisplayStore } from "../../modules/homeDisplay/stores";
import { useCurrencyIcon, useCurrencySymbol } from "../../hooks/useUtils";
import { useNavigation, ROUTES } from "../../utils/navigation";
import { useGlobalValue } from "../../stores/GlobalState";
import { getCountryIdFromStorage } from "../../utils/countryCookie";
import { triggerPopup } from "../../utils/popupTriggers";
import { POPUP_CONFIGS } from "../../config/popupConfig";
import "./Header.scss";
import type { HeaderProps } from "./Header.types";
import type { RedirectConfigItem } from "../../modules/homeDisplay/types";
import GiftImg from "@/assets/header/Gift.png";
import CountryRingImg from "@/assets/header/country-ring.png";
import { utils } from "../..";

const FLAG_ICONS_CDN =
  "https://cdn.jsdelivr.net/npm/flag-icons@7.5.0/flags/1x1";

export const Header: React.FC<HeaderProps> = ({
  title,
  className = "",
  onNotificationClick,
}) => {
  const { t } = useI18n();
  const { navigate } = useNavigation();
  const { isAuthenticated, data, firstRechargeFlag, firstRechargeRuleAmount } =
    useUserDataFlattened();
  const currencyIcon = useCurrencyIcon();
  const balance = data?.balance;
  const currencySymbol = useCurrencySymbol();

  const platformLogo = usePlatformLogo();
  const logoSrc = utils.getImageUrl(platformLogo?.logoUrl || "");
  const redirectConfigs = useHomeDisplayStore(
    state => state.data?.redirectConfigs ?? []
  );
  const hasCountryList =
    Array.isArray(redirectConfigs) && redirectConfigs.length > 0;

  const { currentCountryDisplayName, currentCountryFlagCode } = useMemo(() => {
    const countryId =
      (utils.getObject("countryId") as string) ||
      getCountryIdFromStorage() ||
      (window._config?.countryId ?? "");
    const item = redirectConfigs.find(
      (c: RedirectConfigItem) =>
        (c.countryRedirectId ?? "").trim().toUpperCase() ===
        countryId.trim().toUpperCase()
    );
    const name =
      item?.countryName ?? item?.displayName ?? item?.name ?? item?.id ?? "";
    const raw = (item?.countryRedirectId ?? countryId ?? "").toString().trim();
    const flagCode = raw.length >= 2 ? raw.slice(0, 2).toLowerCase() : null;
    if (name) {
      return {
        currentCountryDisplayName: name,
        currentCountryFlagCode: flagCode,
      };
    }
    if (countryId.trim()) {
      const fromConfig = window._config?.countryList?.find(
        (c: { id?: string }) =>
          (c.id ?? "").trim().toUpperCase() === countryId.trim().toUpperCase()
      );
      const displayName =
        fromConfig?.name ?? fromConfig?.displayName ?? countryId.trim();
      const code = (countryId ?? "").slice(0, 2).toLowerCase();
      return {
        currentCountryDisplayName: displayName,
        currentCountryFlagCode: code || null,
      };
    }
    return { currentCountryDisplayName: "", currentCountryFlagCode: null };
  }, [redirectConfigs]);

  const displayBonus = useMemo(() => {
    if ((!firstRechargeFlag && firstRechargeRuleAmount) || 0 > 0) {
      return `+ ${currencySymbol}${firstRechargeRuleAmount}`;
    }
    return "";
  }, [firstRechargeRuleAmount, currencySymbol, firstRechargeFlag]);

  // 监听 DownloadAppBar 的显示状态
  const [downloadAppBarVisible] = useGlobalValue("downloadAppBarVisible", true);
  // 检查用户是否已注册过
  const hasRegistered = utils.getObject("has_registered");

  const handleLoginClick = () => {
    navigate(ROUTES.LOGIN);
  };

  const handleRegisterClick = () => {
    navigate(ROUTES.REGISTER);
  };

  const handleBalanceClick = () => {
    navigate(ROUTES.ACCOUNT_DETAILS);
  };

  const handleGiftClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止事件冒泡
    console.log("Gift clicked");
    // TODO: 实现礼物功能
  };

  const handleDepositClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止事件冒泡
    navigate(ROUTES.RECHARGE);
  };

  const handleMessageClick = () => {
    navigate(`${ROUTES.MESSAGE_CENTER}?tabIndex=2`);
    if (onNotificationClick) {
      onNotificationClick();
    }
  };

  const handleCountryClick = () => {
    triggerPopup(POPUP_CONFIGS.COUNTRY_LIST.id);
  };

  const displayTitle = title || t("common.title");

  return (
    <header
      className={`mobile-header ${className}`}
      style={{ marginTop: downloadAppBarVisible ? "3rem" : "0" }}
    >
      <div className="mobile-header__content">
        {/* 左侧区域：Logo */}
        <div className="mobile-header__left">
          <div className="mobile-header__logo">
            <OptimizedImage
              loading="eager"
              src={logoSrc}
              alt={displayTitle}
              // width={"3.375rem"}
              height={"1.5rem"}
              priority={true}
            />
          </div>
        </div>

        {/* 右侧功能区 */}
        <div className="mobile-header__actions">
          {hasCountryList && (
            <div
              className="mobile-header__country"
              role="button"
              tabIndex={0}
              onClick={handleCountryClick}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleCountryClick();
                }
              }}
              aria-label={currentCountryDisplayName || "Select country"}
            >
              <span className="mobile-header__country-frame">
                {currentCountryFlagCode && (
                  <img
                    src={`${FLAG_ICONS_CDN}/${currentCountryFlagCode}.svg`}
                    alt=""
                    className="mobile-header__country-flag"
                    width={30}
                    height={30}
                  />
                )}
                <img
                  src={CountryRingImg}
                  alt=""
                  className="mobile-header__country-ring"
                  width={32}
                  height={32}
                  aria-hidden
                />
              </span>
            </div>
          )}
          {isAuthenticated ? (
            <>
              {/* 金币显示区域 */}
              <div
                className="mobile-header__balance-wrapper"
                onClick={handleBalanceClick}
              >
                {/* 礼物按钮 */}
                {displayBonus && (
                  <div
                    className="mobile-header__gift-btn"
                    onClick={handleGiftClick}
                  >
                    <OptimizedImage
                      loading="eager"
                      src={GiftImg}
                      alt="Gift"
                      className="gift-icon"
                      priority={true}
                    />
                    {displayBonus}
                  </div>
                )}

                {/* 余额 */}
                <div className="mobile-header__balance">
                  <OptimizedImage
                    loading="eager"
                    src={utils.getImageUrl(currencyIcon ?? "")}
                    alt="Coin"
                    // width={14}
                    // height={14}
                    className="coincoin-coin"
                    priority={true}
                  />
                  <span className="balance-amount">
                    {typeof balance === "number" && balance != null
                      ? balance.toString().split(".")[1]?.length > 2
                        ? balance.toFixed(2)
                        : balance
                      : balance}
                  </span>
                  {/* <div className="mobile-header__refresh">
                    <Icon
                      name="refresh"
                      size={14}
                      variant="golden"
                      onClick={onRefreshClick}
                    />
                  </div> */}
                </div>
                {/* 充值按钮 */}
                <Button
                  variant="primary"
                  size="sm"
                  className="mobile-header__deposit-btn"
                  onClick={handleDepositClick}
                >
                  {t("common.deposit")}
                </Button>
              </div>

              {/* 邮件图标 */}
              <div className="mobile-header__mail">
                <MailIcon
                  size={24}
                  color="#fff9c7"
                  onClick={handleMessageClick}
                  showCount={true}
                  maxCount={99}
                />
              </div>
            </>
          ) : (
            <>
              {/* 未登录按钮组 */}
              <Button
                variant="outline"
                size="sm"
                className="mobile-header__login-btn"
                onClick={handleLoginClick}
              >
                {t("common.logIn")}
              </Button>
              <div className="mobile-header__register-btn-group">
                <Button
                  variant="primary"
                  size="sm"
                  className={`mobile-header__register-btn ${!hasRegistered ? "mobile-header__register-btn--pulse" : ""}`}
                  onClick={handleRegisterClick}
                >
                  {t("common.register")}
                </Button>
                {displayBonus && (
                  <div
                    className="mobile-header__gift-btn"
                    onClick={handleGiftClick}
                  >
                    <OptimizedImage
                      loading="eager"
                      src={GiftImg}
                      alt="Gift"
                      className="gift-icon"
                      priority={true}
                    />
                    {displayBonus}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
